/* eslint-disable @typescript-eslint/no-magic-numbers */
/*
 * Display 3js shapes in the browser dev console. Not used in the Flux production codebase, but
 * very useful for debugging.
 */
import { cloneDeep } from "lodash";
function autoScale(paths) {
    const result = cloneDeep(paths);
    const { minX, maxX, minY, maxY } = getBounds(paths);
    // Calculate the scaling factor
    const scaleX = 100 / (maxX - minX);
    const scaleY = 100 / (maxY - minY);
    const scale = Math.min(scaleX, scaleY);
    for (const path of result) {
        for (const vector of path) {
            vector.x = (vector.x - minX) * scale;
            vector.y = (vector.y - minY) * scale;
        }
    }
    return result;
}
function toSvgPath(paths) {
    return paths.map((p) => "M" + p.map((v) => `${roundFloat(v.x, 9)},${-roundFloat(v.y, 9)}`) + "z").join(" ");
}
function toSingleSvgPath(shape) {
    return "M" + shape.map((v) => `${roundFloat(v.x, 9)},${-roundFloat(v.y, 9)}`) + "z";
}
export function getBounds(paths) {
    // Find the minimum and maximum x and y values
    let minX = Number.MAX_VALUE;
    let minY = Number.MAX_VALUE;
    let maxX = -Number.MAX_VALUE;
    let maxY = -Number.MAX_VALUE;
    for (const vectors of paths) {
        for (const vector of vectors) {
            minX = Math.min(minX, vector.x);
            minY = Math.min(minY, vector.y);
            maxX = Math.max(maxX, vector.x);
            maxY = Math.max(maxY, vector.y);
        }
    }
    return { minX, maxX, minY, maxY };
}
function getSVGViewbox(paths) {
    let { minX, maxX, minY, maxY } = getBounds(paths);
    // Convert to SVG space (swap y direction)
    [minY, maxY] = [-maxY, -minY];
    // Calculate the viewbox values
    const x = minX;
    const y = minY;
    const width = maxX - minX;
    const height = maxY - minY;
    // Return the viewbox as a string
    return `${x} ${y} ${width} ${height}`;
}
/** Get info separately from actual logging, so we can unit test it without exporting
 * all the above private functions. */
export function getShapeInfoToLog(paths) {
    const scaledPath = autoScale(paths);
    return {
        scaledPath: scaledPath,
        viewBox: getSVGViewbox(scaledPath),
        svgPath: toSvgPath(scaledPath),
    };
}
function renderSvg(paths, options = { fill: "blue", stroke: "none", printSvgString: false }) {
    const { viewBox, svgPath } = getShapeInfoToLog(paths);
    const svg = `<svg version="1.1" id="Layer_1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" x="0px" y="0px" viewBox="${viewBox}" xml:space="preserve">
        <path d='${svgPath}' fill="${options.fill}" stroke="${options.stroke}" />
        </svg>
    `;
    return svg;
}
function renderSvgs(shapes, options) {
    const scaledShapes = autoScale(shapes);
    const viewBox = getSVGViewbox(scaledShapes);
    const svg = `<svg version="1.1" id="Layer_1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" x="0px" y="0px" viewBox="${viewBox}" xml:space="preserve">
        ${scaledShapes
        .map((shape) => `<path d="${toSingleSvgPath(shape)}" stroke="${options.stroke}" fill="${options.fill}" stroke-width="0.1" />`)
        .join("\n")}
    </svg>`;
    return svg;
}
export function logShapeToConsole(paths, withMessage, options = { fill: "blue", stroke: "none", printSvgString: false }) {
    const svg = renderSvg(paths, options);
    const svgDataUrl = `data:image/svg+xml;base64,${btoa(svg)}`;
    const css = `
        background-image: url(${svgDataUrl});
        padding-bottom: 300px;
        padding-left: 300px;
        background-size: contain;
        background-position: center center;
        background-repeat: no-repeat;
    `;
    if (options.printSvgString) {
        // eslint-disable-next-line no-console
        console.log("SVG String", svg);
    }
    // eslint-disable-next-line no-console
    console.log(`%c${withMessage ?? " "}`, css);
}
const defaultOptions = {
    fill: "rgba(0,0,255,0.1)",
    stroke: "black",
    printSvgString: false,
};
export function logShapesToConsole(shapes, withMessage, options = {}) {
    options = { ...defaultOptions, ...options };
    const svg = renderSvgs(shapes, options);
    const svgDataUrl = `data:image/svg+xml;base64,${btoa(svg)}`;
    const css = `
        background-image: url(${svgDataUrl});
        padding-bottom: 300px;
        padding-left: 300px;
        background-size: contain;
        background-position: center center;
        background-repeat: no-repeat;
    `;
    if (options.printSvgString) {
        // eslint-disable-next-line no-console
        console.log("SVG String", svg);
    }
    // eslint-disable-next-line no-console
    console.log(`%c${withMessage ?? " "}`, css);
}
export function logThreeJSShapeToConsole(shape, withMessage) {
    const extracted = shape.extractPoints(64);
    logShapeToConsole([extracted.shape, ...extracted.holes], withMessage);
}
function roundFloat(n, p) {
    const precision = Math.pow(10, p);
    return Math.round(n * precision) / precision;
}
//# sourceMappingURL=logShapeToConsole.js.map